// Ported from dev-scrips-pm-react services/scheduling/use-available-slots.ts (PROD-1190 calendar port)
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { SlotResponse } from '@/types/scheduling-api';

/**
 * POST /api/v1/Practice/Appointment/Slots
 *
 * Mirrors Flutter `AvailableSlotsRequest.toJson()` —
 * `available_slots_request.dart`. The backend expects local-clock
 * datetime strings without a Z suffix; using `Date.toISOString()` in
 * UTC shifts the day for users east of UTC.
 */

interface SlotsParams {
  practiceId: string;
  practitionerId: string;
  appointmentProfileId?: string;
  /** Any ISO date — the hook expands to the local 00:00:00.000–23:59:59.000 day range. */
  start: string;
  end: string;
  count?: number;
}

/** Format a Date as local-clock `YYYY-MM-DDTHH:mm:ss.sss` (no Z). */
function toLocalClockString(d: Date): string {
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const h = pad(d.getHours());
  const min = pad(d.getMinutes());
  const s = pad(d.getSeconds());
  const ms = pad(d.getMilliseconds(), 3);
  return `${y}-${m}-${day}T${h}:${min}:${s}.${ms}`;
}

function localDayRange(anchor: string): { start: string; end: string } {
  const d = new Date(anchor);
  const start = new Date(d);
  start.setHours(0, 0, 0, 0);
  const end = new Date(d);
  end.setHours(23, 59, 59, 0);
  return { start: toLocalClockString(start), end: toLocalClockString(end) };
}

export function useAvailableSlots(params: SlotsParams | null) {
  return useQuery({
    queryKey: ['available-slots', params],
    queryFn: ({ signal }) => {
      if (!params) throw new Error('useAvailableSlots called without params');
      const { start, end } = localDayRange(params.start);
      const body = {
        practiceId: params.practiceId,
        practitionerId: params.practitionerId,
        ...(params.appointmentProfileId
          ? { appointmentProfileId: params.appointmentProfileId }
          : {}),
        start,
        end,
        count: params.count ?? 100,
        searchFromUserApp: false,
      };
      return post<SlotResponse[]>('/api/v1/Practice/Appointment/Slots', body, { signal });
    },
    enabled: !!params?.practitionerId && !!params?.practiceId,
  });
}
